import { geometry as g } from '@progress/kendo-drawing';
import { defined } from './../utils';
import { Point, Rect } from './../math';
import { deepExtend } from './../utils/deep-extend';
import { CompositeTransform } from './CompositeTransform';
import { Rotation } from './Rotation';
import { Translation } from './Translation';
import { diffNumericOptions } from './diffNumericOptions';
/**
 * Base class for all visual elements in the diagram system.
 * Provides core functionality for positioning, transformation, and drawing operations.
 */
export class Element {
    /**
     * Creates a new Element instance.
     * @param options Configuration options for the element
     */
    constructor(options) {
        this.options = deepExtend({}, this.options, options);
        this.id = this.options.id;
        this._originSize = Rect.empty();
        this._transform = new CompositeTransform();
    }
    /**
     * Gets or sets the visibility of the element.
     * @param value The visibility value to set, or undefined to get current visibility
     * @returns The current visibility state when called as getter
     */
    visible(value) {
        return this.drawingContainer().visible(value);
    }
    /**
     * Redraws the element with updated options.
     * @param options New options to apply to the element
     */
    redraw(options) {
        if (options && options.id) {
            this.id = options.id;
        }
    }
    /**
     * Gets or sets the position of the element.
     * @param x The x coordinate or Point object, or undefined to get current position
     * @param y The y coordinate when x is a number
     * @returns The current position as a Point when called as getter
     */
    position(x, y) {
        const options = this.options;
        if (!defined(x)) {
            return new Point(options.x, options.y);
        }
        if (defined(y)) {
            options.x = x;
            options.y = y;
        }
        else if (x instanceof Point) {
            options.x = x.x;
            options.y = x.y;
        }
        this._transform.translate = new Translation(options.x, options.y);
        this._renderTransform();
    }
    /**
     * Gets or sets the rotation of the element.
     * @param angle The rotation angle in radians, or undefined to get current rotation
     * @param center The center point for rotation
     * @returns The current rotation when called as getter
     */
    rotate(angle, center) {
        if (defined(angle)) {
            this._transform.rotate = new Rotation(angle, center.x, center.y);
            this._renderTransform();
        }
        return this._transform.rotate || Rotation.ZERO;
    }
    /**
     * Gets the drawing container for this element.
     * @returns The drawing element container
     */
    drawingContainer() {
        return this.drawingElement;
    }
    /** @hidden */
    _renderTransform() {
        const matrix = this._transform.toMatrix();
        this.drawingContainer().transform(new g.Matrix(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f));
    }
    /** @hidden */
    _hover() { }
    /** @hidden */
    _diffNumericOptions(options, fields) {
        return diffNumericOptions.call(this, options, fields);
    }
    /** @hidden */
    _measure(force) {
        let rect;
        if (!this._measured || force) {
            const box = this._boundingBox() || new g.Rect([0, 0], [0, 0]);
            const startPoint = box.topLeft();
            rect = new Rect(startPoint.x, startPoint.y, box.width(), box.height());
            this._originSize = rect;
            this._originWidth = rect.width;
            this._originHeight = rect.height;
            this._measured = true;
        }
        else {
            rect = this._originSize;
        }
        return rect;
    }
    /** @hidden */
    _boundingBox() {
        return this.drawingElement.rawBBox();
    }
    /** @hidden */
    _initAccessibility() {
        const accessibility = this.options.accessibility;
        if (accessibility) {
            this.drawingElement.options.set('role', accessibility.role);
            this.drawingElement.options.set('ariaRoleDescription', accessibility.ariaRoleDescription);
            this.drawingElement.options.set('ariaLabel', accessibility.ariaLabel);
            this.drawingElement.options.set('id', this.options.id);
        }
    }
}
